import React, { Fragment } from "react";
import { Box, Divider, ListItem, ListItemText, Switch, Typography } from "@mui/material";
import { Trans } from "@lingui/macro";
import { useConfigContext } from "@WahooFitness/wahoo-offline-mfe";
import useRecoveryDevicePicker from "./useRecoveryDevicePicker";
import RecoveryDeviceElsewhereMessage from "./RecoveryDeviceElsewhereMessage";
import { WAHOO_APP_ANDROID_FITNESS_APP_ID } from "./wahooAppFitnessAppIds";

// Under Apple Health while it is the recovery source (design "Apple health. Multiple devices."): the
// devices in Apple Health with HRV (TAJ-1628), in an inset card, one switch each, exactly one on.
// Renders nothing until native answers (never on web); with no device that tracks HRV, a note saying
// so, unless a query failed and a device may only be missing.
// When the account's device is on another phone (always on Android), it heads the card switched on, with
// a note saying where it is, and every device here is off; choosing one confirms first (TAJ-1641).
const RecoveryDeviceList: React.FC = () => {
  const { sources, locked, hrvOnly, noHrvDevices, elsewhereDevice, isSelected, handleSelect } =
    useRecoveryDevicePicker(true);
  const { fitnessAppId } = useConfigContext();
  // Android has no Apple Health and lists no devices of its own, so its empty list says nothing about
  // HRV devices: the note below is about this phone's Apple Health, which only iOS has read.
  const listsOwnDevices = fitnessAppId !== WAHOO_APP_ANDROID_FITNESS_APP_ID;

  if (noHrvDevices && listsOwnDevices) {
    return (
      <Box px={2} pb={2}>
        <Typography
          variant="prose-sm"
          color="text.secondary"
          component="p"
          data-testid="recovery-device-empty"
        >
          <Trans>
            No overnight HRV device was found. Make sure your device is worn while sleeping and
            supports beat-to-beat (rMSSD) tracking. This is required to calculate your recovery
            response.
          </Trans>
        </Typography>
      </Box>
    );
  }

  if (sources.length === 0 && !elsewhereDevice) {
    return null;
  }

  return (
    <Box px={2} pb={2}>
      {elsewhereDevice ? (
        <Typography
          variant="prose-sm"
          color="text.secondary"
          component="p"
          pb={1.5}
          data-testid="recovery-device-elsewhere"
        >
          <RecoveryDeviceElsewhereMessage />
        </Typography>
      ) : (
        // The choice is only explained with two or more devices: a lone one is locked on.
        sources.length > 1 && (
          <Typography
            variant="prose-sm"
            color="text.secondary"
            component="p"
            pb={1.5}
            data-testid="recovery-device-help"
          >
            {hrvOnly ? (
              <Trans>
                Multiple devices that read Recovery HRV are connected to Apple Health. Choose which
                one Wahoo should read HRV data from.
              </Trans>
            ) : (
              <Trans>
                Multiple devices are connected to Apple Health. Choose which one Wahoo should read
                data from.
              </Trans>
            )}
          </Typography>
        )
      )}
      <Box border={1} borderColor="divider" borderRadius={2} overflow="hidden">
        {elsewhereDevice && (
          // The account's device, on and locked: it can only be changed on the phone that holds it.
          <ListItem data-testid="recovery-device-account-row">
            <ListItemText
              primary={elsewhereDevice.name}
              primaryTypographyProps={{ variant: "prose-base" }}
            />
            <Switch
              checked
              color="info"
              disabled
              inputProps={{ "aria-label": elsewhereDevice.name }}
            />
          </ListItem>
        )}
        {sources.map((source, index) => (
          <Fragment key={source.id}>
            {(index > 0 || !!elsewhereDevice) && <Divider />}
            <ListItem data-testid="recovery-device-row">
              <ListItemText
                primary={source.name}
                primaryTypographyProps={{ variant: "prose-base" }}
              />
              {/* Under another phone's device every switch is off and stays enabled, so a tap says
                  why it can't be chosen here: the picker opens the drawer instead of choosing. */}
              <Switch
                checked={isSelected(source)}
                onChange={() => handleSelect(source)}
                color="info"
                disabled={locked}
                inputProps={{ "aria-label": source.name }}
              />
            </ListItem>
          </Fragment>
        ))}
      </Box>
    </Box>
  );
};

export default RecoveryDeviceList;
